import type { JSX } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { PendoId, PendoProps } from "@impero-com/ui";

import { CategoryAndTag } from "api/generated/model";

import { ControlProgramFilter } from "./ControlProgramFilter";
import { EntitiesFilter } from "./EntitiesFilter";
import { FilterType, localizeFilterType } from "./filterType";
import { FilterTypeRow } from "./FilterTypeRow";
import { TagsFilter } from "./TagsFilter";
import { TagCategory } from "./types";

interface FilterRowProps extends PendoProps {
    removeButtonPendoId?: PendoId;
    filterType: FilterType;
    index: number;
    enabledFilterTypes: FilterType[];
    selectedFilters: FilterType[];
    onFilterRemove: (filterType: FilterType) => void;
    onFilterTypeChange: (filterType: FilterType, index: number) => void;

    // Filter-specific props
    selectedTags: CategoryAndTag[];
    onTagsChange: (tags: CategoryAndTag[]) => void;
    selectedControlPrograms: number[];
    onControlProgramsChange: (programs: number[]) => void;
    selectedEntities: number[];
    onEntitiesChange: (value: number[]) => void;
    tagCategories: TagCategory[];
}

export function FilterRow({
    filterType,
    index,
    enabledFilterTypes,
    selectedFilters,
    onFilterRemove,
    onFilterTypeChange,
    selectedTags,
    onTagsChange,
    selectedControlPrograms,
    onControlProgramsChange,
    selectedEntities,
    onEntitiesChange,
    tagCategories,
    "data-pendo": dataPendo,
    removeButtonPendoId,
}: FilterRowProps) {
    const { t } = useTranslation();

    const renderFilterComponent = (): JSX.Element => {
        switch (filterType) {
            case "Tags":
                return (
                    <TagsFilter
                        placeholder={t("Dashboard_TagFilter_Placeholder")}
                        tagCategories={tagCategories}
                        value={selectedTags}
                        onChange={onTagsChange}
                    />
                );
            case "ControlPrograms":
                return (
                    <ControlProgramFilter
                        value={selectedControlPrograms}
                        onChange={onControlProgramsChange}
                    />
                );
            case "Entities":
                return (
                    <EntitiesFilter
                        value={selectedEntities}
                        onChange={onEntitiesChange}
                    />
                );
            default:
                return assertNever(filterType);
        }
    };

    return (
        <FilterTypeRow
            data-pendo={dataPendo}
            removeButtonPendoId={removeButtonPendoId}
            filterType={filterType}
            index={index}
            enabledFilterTypes={enabledFilterTypes}
            selectedFilterTypes={selectedFilters}
            localizeFilterType={(type) => localizeFilterType(type, t)}
            onFilterRemove={onFilterRemove}
            onFilterTypeChange={onFilterTypeChange}
        >
            {renderFilterComponent()}
        </FilterTypeRow>
    );
}
